import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { monthLabel, stateLabel, useGstMonths } from '@/lib/gst';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { useOrgLocale } from '@/lib/org-locale';
import { useCompanySettings } from '@/lib/settings';

export const Route = createFileRoute('/taxes/gst/')({ component: GstCentre });

const REPORTS = ['gstr-1', 'gstr-3b', 'gst-detail', 'itc-summary', 'hsn-summary'] as const;

/** Screen T6 (document 06): Taxes › GST for India clients, one row per month. */
function GstCentre() {
  const t = useT();
  const orgId = useOrgId();
  const { taxPack, currency } = useOrgLocale();
  const months = useGstMonths();
  const settings = useCompanySettings();
  const gst = settings.data?.gst;

  return (
    <>
      <PageHeader
        title={t('taxPages.gst.title')}
        description={t('taxPages.gst.description')}
        actions={
          taxPack === 'in' && orgId ? (
            <>
              <Button asChild variant="outline">
                <Link to="/taxes/gst/ims">{t('taxPages.gst.imsLink')}</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/taxes/gst/eway-bills">{t('taxPages.gst.ewayLink')}</Link>
              </Button>
            </>
          ) : undefined
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : taxPack !== 'in' ? (
        <EmptyState
          title={t('taxPages.gst.indiaOnlyTitle')}
          hint={t('taxPages.gst.indiaOnlyHint')}
          action={
            <Button asChild variant="outline">
              <Link to="/taxes/vat">{t('taxPages.vatShared.title')}</Link>
            </Button>
          }
        />
      ) : months.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : months.isError ? (
        <EmptyState title={t('taxPages.gst.loadError')} hint={months.error.message} />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-x-8 gap-y-1 rounded-lg border bg-card px-4 py-3 text-sm">
            <span>
              <span className="text-muted-foreground">GSTIN </span>
              <span className="tnum font-medium">
                {months.data.gstin ?? t('taxPages.gst.notRegistered')}
              </span>
            </span>
            <span>
              <span className="text-muted-foreground">{t('settingsPages.general.gst.state')} </span>
              <span className="font-medium">{stateLabel(months.data.stateCode)}</span>
            </span>
            <span>
              <span className="text-muted-foreground">
                {t('taxPages.vatShared.filingFrequency')}{' '}
              </span>
              <span className="font-medium">
                {gst?.taxPeriod === 'quarterly'
                  ? t('taxPages.vatShared.quarterly')
                  : t('taxPages.vatShared.monthly')}
              </span>
            </span>
          </div>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('taxPages.vatHome.colPeriod')}</TableHead>
                  <TableHead className="w-[90px] text-end">
                    {t('taxPages.gst.colDocuments')}
                  </TableHead>
                  <TableHead className="w-[150px] text-end">
                    {t('taxPages.gst.colOutwardTaxable')}
                  </TableHead>
                  <TableHead className="w-[130px] text-end">
                    {t('taxPages.gst.colOutputTax')}
                  </TableHead>
                  <TableHead className="w-[130px] text-end">{t('taxPages.gst.colItc')}</TableHead>
                  <TableHead className="w-[140px] text-end">
                    {t('taxPages.gst.colCashPayable')}
                  </TableHead>
                  <TableHead className="w-[150px]">{t('taxPages.vatHome.colDue')}</TableHead>
                  <TableHead className="w-[200px] text-end">
                    {t('taxPages.vatTabs.returns')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {months.data.months.map((m) => (
                  <TableRow key={m.period} data-testid="gst-month">
                    <TableCell>
                      <div className="font-medium">{monthLabel(m.period)}</div>
                      {m.findings ? (
                        <div className="inline-flex items-center gap-1 text-xs text-[#B45309]">
                          <AlertTriangle className="size-3.5" aria-hidden />
                          {t('taxPages.gst.exceptions', { count: m.findings })}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="tnum text-end">{m.documents}</TableCell>
                    <TableCell className="tnum text-end">
                      {formatAmount(m.outwardTaxable)}
                    </TableCell>
                    <TableCell className="tnum text-end">{formatAmount(m.outputTax)}</TableCell>
                    <TableCell className="tnum text-end">{formatAmount(m.itc)}</TableCell>
                    <TableCell className="tnum text-end font-medium">
                      {currency} {formatAmount(m.cashPayable)}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div>GSTR-1 {displayDate(m.gstr1Due)}</div>
                      <div>GSTR-3B {displayDate(m.gstr3bDue)}</div>
                    </TableCell>
                    <TableCell className="text-end">
                      <Button asChild size="sm" variant="link" className="px-2">
                        <Link to="/taxes/gst/gstr1" search={{ period: m.period }}>
                          GSTR-1
                        </Link>
                      </Button>
                      <Button asChild size="sm" variant="link" className="px-2">
                        <Link to="/taxes/gst/gstr3b" search={{ period: m.period }}>
                          GSTR-3B
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <h2 className="mt-6 mb-2 text-base font-semibold">{t('taxPages.gst.reports')}</h2>
          <ul className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            {REPORTS.map((key) => (
              <li key={key}>
                <Link
                  to="/reports/$reportKey"
                  params={{ reportKey: key }}
                  className="text-primary hover:underline"
                >
                  {t(`reportPages.reports.catalogue.names.${key}`)}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
